import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { BinderCoverPreview } from "@/components/BinderCoverPreview";

const CURRENCIES = ["CAD", "USD", "EUR", "GBP", "JPY", "AUD"];
const FYE_OPTIONS = [
  { value: "", label: "Not set" },
  { value: "01-31", label: "Jan 31" }, { value: "02-28", label: "Feb 28/29" },
  { value: "03-31", label: "Mar 31" }, { value: "04-30", label: "Apr 30" },
  { value: "05-31", label: "May 31" }, { value: "06-30", label: "Jun 30" },
  { value: "07-31", label: "Jul 31" }, { value: "08-31", label: "Aug 31" },
  { value: "09-30", label: "Sep 30" }, { value: "10-31", label: "Oct 31" },
  { value: "11-30", label: "Nov 30" }, { value: "12-31", label: "Dec 31" },
];

const Section = ({ title, children, testid }) => (
  <div data-testid={testid} style={{
    border: "1px solid var(--ink-line)", borderRadius: 8, padding: 18, marginBottom: 16,
  }}>
    <div style={{ fontWeight: 700, marginBottom: 12, color: "var(--ink)" }}>{title}</div>
    {children}
  </div>
);

const Field = ({ label, children, testid }) => (
  <label data-testid={testid} style={{ display: "block", marginBottom: 10 }}>
    <div style={{ fontSize: 12, color: "var(--ink-mute)", marginBottom: 4 }}>{label}</div>
    {children}
  </label>
);

const Input = (props) => (
  <input
    {...props}
    style={{
      width: "100%", padding: "8px 10px",
      border: "1px solid var(--ink-line)", borderRadius: 6,
      fontSize: 14, background: "white", ...(props.style || {}),
    }}
  />
);

const Select = ({ children, ...props }) => (
  <select
    {...props}
    style={{
      width: "100%", padding: "8px 10px",
      border: "1px solid var(--ink-line)", borderRadius: 6,
      fontSize: 14, background: "white", ...(props.style || {}),
    }}
  >
    {children}
  </select>
);

const ColorPicker = ({ label, value, onChange, testid, contrastAgainst }) => {
  const [warn, setWarn] = useState(null);
  useEffect(() => {
    if (!contrastAgainst) return;
    let active = true;
    api.checkContrast(value || "#000000", contrastAgainst).then(r => {
      if (!active) return;
      setWarn(r.wcag_aa ? null : `Contrast ${r.ratio} against ${contrastAgainst} fails WCAG AA (≥4.5).`);
    }).catch(() => {});
    return () => { active = false; };
  }, [value, contrastAgainst]);
  return (
    <Field label={label} testid={testid}>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <input
          type="color" value={value || "#000000"} onChange={e => onChange(e.target.value)}
          style={{ width: 42, height: 36, border: "1px solid var(--ink-line)", borderRadius: 6, padding: 0 }}
          data-testid={`${testid}-swatch`}
        />
        <Input
          value={value || ""}
          onChange={e => onChange(e.target.value)}
          placeholder="#1f5e3a"
          data-testid={`${testid}-hex`}
        />
      </div>
      {warn && <div style={{ color: "#a64a00", fontSize: 12, marginTop: 4 }}>{warn}</div>}
    </Field>
  );
};

export default function CompanySettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [legalSameAsCompany, setLegalSameAsCompany] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const s = await api.getSettings();
      setSettings(s);
      // Initial checkbox state: true when both blank OR identical to company_name.
      setLegalSameAsCompany(
        (!s.company_name && !s.legal_name) || s.company_name === s.legal_name
      );
    }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const patch = (path, value) => {
    setSettings(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      const keys = path.split(".");
      let cur = next;
      for (let i = 0; i < keys.length - 1; i++) {
        cur[keys[i]] = cur[keys[i]] ?? {};
        cur = cur[keys[i]];
      }
      cur[keys.at(-1)] = value;
      // Mirror company_name → legal_name while the checkbox is on.
      if (path === "company_name" && legalSameAsCompany) {
        next.legal_name = value;
      }
      return next;
    });
  };

  const onToggleLegalSame = (checked) => {
    setLegalSameAsCompany(checked);
    if (checked && settings) {
      // Snap legal_name to current company_name immediately.
      setSettings(prev => ({ ...prev, legal_name: prev.company_name || "" }));
    }
  };

  const onLogoUpload = async (file) => {
    if (!file) return;
    try {
      setSaving(true);
      const upload = await api.uploadLogo(file);
      setSettings(prev => ({ ...prev, logo_asset_id: upload.asset_id }));
      setMsg({ type: "ok", text: `Logo uploaded · ${upload.size_bytes} bytes` });
    } catch (e) {
      setMsg({ type: "err", text: e.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  };

  const onLogoDelete = async () => {
    try {
      setSaving(true);
      await api.deleteLogo();
      setSettings(prev => ({ ...prev, logo_asset_id: null }));
      setMsg({ type: "ok", text: "Logo removed." });
    } finally { setSaving(false); }
  };

  const onSave = async () => {
    try {
      setSaving(true); setMsg(null);
      const payload = {
        company_name: settings.company_name,
        legal_name: settings.legal_name,
        default_currency: settings.default_currency,
        fiscal_year_end: settings.fiscal_year_end,
        default_materiality_threshold: Number(settings.default_materiality_threshold) || 0,
        brand_theme: settings.brand_theme,
        address: settings.address,
        contact: settings.contact,
      };
      const saved = await api.saveSettings(payload);
      setSettings(saved);
      setMsg({ type: "ok", text: "Settings saved. Downstream reports will use the new values." });
    } catch (e) {
      setMsg({ type: "err", text: e.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  };

  if (loading || !settings) return <div className="empty">Loading settings…</div>;

  return (
    <div data-testid="page-company-settings" style={{ maxWidth: 1280 }}>
      <div className="heading h1">Company Settings</div>
      <div className="muted" style={{ marginTop: 4, marginBottom: 18, fontSize: 14 }}>
        Identity, branding, and contact details that flow into every binder and report.
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 360px", gap: 20, alignItems: "flex-start" }}>
        {/* Column 1: Identity + Branding */}
        <div>
          <Section title="Identity" testid="section-identity">
            <Field label="Company name" testid="field-company-name">
              <Input value={settings.company_name} onChange={e => patch("company_name", e.target.value)} data-testid="input-company-name" />
            </Field>
            <Field label="Legal name" testid="field-legal-name">
              <Input
                value={settings.legal_name}
                onChange={e => patch("legal_name", e.target.value)}
                disabled={legalSameAsCompany}
                style={legalSameAsCompany ? { background: "#f5f3ee", color: "var(--ink-mute)" } : undefined}
                data-testid="input-legal-name"
              />
              <label style={{
                display: "flex", alignItems: "center", gap: 6,
                marginTop: 6, fontSize: 12, color: "var(--ink-mute)", cursor: "pointer",
              }} data-testid="field-legal-same-as-company">
                <input
                  type="checkbox"
                  checked={legalSameAsCompany}
                  onChange={e => onToggleLegalSame(e.target.checked)}
                  data-testid="input-legal-same-as-company"
                />
                Same as company name
              </label>
            </Field>
            <Field label="Default currency" testid="field-default-currency">
              <Select value={settings.default_currency} onChange={e => patch("default_currency", e.target.value)} data-testid="input-default-currency">
                {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
              </Select>
            </Field>
            <Field label="Fiscal year end" testid="field-fye">
              <Select value={settings.fiscal_year_end} onChange={e => patch("fiscal_year_end", e.target.value)} data-testid="input-fiscal-year-end">
                {FYE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </Select>
            </Field>
            <Field label="Default materiality threshold ($)" testid="field-materiality">
              <Input
                type="number" min="0" step="0.01"
                value={settings.default_materiality_threshold || 0}
                onChange={e => patch("default_materiality_threshold", e.target.value)}
                data-testid="input-default-materiality"
              />
            </Field>
          </Section>

          <Section title="Branding" testid="section-branding">
            <Field label="Logo" testid="field-logo">
              <div style={{
                display: "flex", alignItems: "center", gap: 12,
                padding: 10, border: "1px dashed var(--ink-line)", borderRadius: 6,
                background: "var(--cream, #fbfaf7)",
              }}>
                {settings.logo_asset_id ? (
                  <img src={api.assetUrl(settings.logo_asset_id)} alt="logo"
                       style={{ height: 48, maxWidth: 160, objectFit: "contain" }} />
                ) : (
                  <div style={{ color: "var(--ink-mute)", fontSize: 12 }}>No logo set.</div>
                )}
                <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
                  <label className="btn" style={{ cursor: "pointer" }} data-testid="logo-upload-trigger">
                    Upload
                    <input
                      type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp"
                      onChange={e => onLogoUpload(e.target.files?.[0])}
                      style={{ display: "none" }}
                      data-testid="input-logo-file"
                    />
                  </label>
                  {settings.logo_asset_id && (
                    <button className="btn" onClick={onLogoDelete} data-testid="logo-delete-btn">Remove</button>
                  )}
                </div>
              </div>
              <div style={{ fontSize: 11, color: "var(--ink-mute)", marginTop: 4 }}>
                SVG preferred · PNG/JPG/WebP accepted · max 500 KB
              </div>
            </Field>
            <ColorPicker
              label="Primary brand color"
              value={settings.brand_theme.primary_color}
              onChange={v => patch("brand_theme.primary_color", v)}
              contrastAgainst={settings.brand_theme.text_on_primary || "#ffffff"}
              testid="color-primary"
            />
            <ColorPicker
              label="Secondary brand color (report background)"
              value={settings.brand_theme.secondary_color}
              onChange={v => patch("brand_theme.secondary_color", v)}
              contrastAgainst={settings.brand_theme.accent_color || "#1a1a1a"}
              testid="color-secondary"
            />
            <ColorPicker
              label="Report accent color (text / borders)"
              value={settings.brand_theme.accent_color}
              onChange={v => patch("brand_theme.accent_color", v)}
              contrastAgainst={settings.brand_theme.secondary_color || "#fbfaf7"}
              testid="color-accent"
            />
            <ColorPicker
              label="Text-on-primary (button labels / banners)"
              value={settings.brand_theme.text_on_primary}
              onChange={v => patch("brand_theme.text_on_primary", v)}
              contrastAgainst={settings.brand_theme.primary_color || "#1f5e3a"}
              testid="color-text-on-primary"
            />
          </Section>
        </div>

        {/* Column 2: Contact + Address */}
        <div>
          <Section title="Preparer" testid="section-contact">
            <Field label="Preparer name" testid="field-contact-name">
              <Input value={settings.contact.contact_name} onChange={e => patch("contact.contact_name", e.target.value)} data-testid="input-contact-name" />
            </Field>
            <Field label="Preparer position" testid="field-contact-position">
              <Input value={settings.contact.contact_position || ""} onChange={e => patch("contact.contact_position", e.target.value)} data-testid="input-contact-position" placeholder="e.g. Controller, Senior Accountant" />
            </Field>
            <Field label="Preparer email" testid="field-contact-email">
              <Input type="email" value={settings.contact.contact_email} onChange={e => patch("contact.contact_email", e.target.value)} data-testid="input-contact-email" />
            </Field>
            <Field label="Preparer phone" testid="field-contact-phone">
              <Input value={settings.contact.contact_phone} onChange={e => patch("contact.contact_phone", e.target.value)} data-testid="input-contact-phone" />
            </Field>
            <Field label="Website" testid="field-contact-website">
              <Input value={settings.contact.website} onChange={e => patch("contact.website", e.target.value)} data-testid="input-website" />
            </Field>
          </Section>

          <Section title="Address" testid="section-address">
            <Field label="Address line 1" testid="field-line1">
              <Input value={settings.address.line1} onChange={e => patch("address.line1", e.target.value)} data-testid="input-line1" />
            </Field>
            <Field label="Address line 2" testid="field-line2">
              <Input value={settings.address.line2} onChange={e => patch("address.line2", e.target.value)} data-testid="input-line2" />
            </Field>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <Field label="City" testid="field-city">
                <Input value={settings.address.city} onChange={e => patch("address.city", e.target.value)} data-testid="input-city" />
              </Field>
              <Field label="Region (state/province)" testid="field-region">
                <Input value={settings.address.region} onChange={e => patch("address.region", e.target.value)} data-testid="input-region" />
              </Field>
              <Field label="Postal code" testid="field-postal">
                <Input value={settings.address.postal_code} onChange={e => patch("address.postal_code", e.target.value)} data-testid="input-postal-code" />
              </Field>
              <Field label="Country" testid="field-country">
                <Input value={settings.address.country} onChange={e => patch("address.country", e.target.value)} data-testid="input-country" />
              </Field>
            </div>
          </Section>
        </div>

        {/* Column 3: Live binder preview */}
        <div style={{ position: "sticky", top: 16 }}>
          <div style={{ fontSize: 12, color: "var(--ink-mute)", marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.4 }}>
            Live preview — certification package cover
          </div>
          <BinderCoverPreview settings={settings} logoAssetId={settings.logo_asset_id} />
          <div style={{ fontSize: 11, color: "var(--ink-mute)", marginTop: 8 }}>
            How the Certification Package cover will render with the current settings.
          </div>
        </div>
      </div>

      <div style={{
        position: "sticky", bottom: 0, background: "white",
        padding: "12px 0", marginTop: 8, display: "flex", gap: 12, alignItems: "center",
        borderTop: "1px solid var(--ink-line)",
      }}>
        <button className="btn primary" onClick={onSave} disabled={saving} data-testid="save-settings-btn">
          {saving ? "Saving…" : "Save Changes"}
        </button>
        <button className="btn" onClick={load} disabled={saving} data-testid="reload-settings-btn">
          Reload
        </button>
        {msg && (
          <div style={{
            fontSize: 13,
            color: msg.type === "ok" ? "#1f5e3a" : "#a30000",
          }} data-testid="settings-msg">
            {msg.text}
          </div>
        )}
      </div>
    </div>
  );
}
